import { useState, useEffect, useMemo } from 'react'; // Added useMemo
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFirebase } from '@/context/FirebaseProvider'; // Changed from useData
import { useToast } from '@/hooks/use-toast';
import { Business } from '@/schemas/business'; 
import { businessUtils } from '@/context/utils/businessUtils'; // Import refactored businessUtils

export const useBusinessOnboarding = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { businesses: allBusinesses, portfolios: allPortfolios, isLoading: firebaseIsLoading } = useFirebase();
  const { toast } = useToast();
  
  const findBusiness = useMemo(() => (id: string | undefined): Business | undefined => {
    if (!id || !allBusinesses) return undefined;
    return allBusinesses.find(b => b.id === id);
  }, [allBusinesses]);
  
  const [business, setBusiness] = useState<Business | undefined>(findBusiness(businessId));
  const [step, setStep] = useState(0);
  // dataLoaded can be determined by !firebaseIsLoading
  const dataLoaded = !firebaseIsLoading; 
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Get the selected package from the business in context, if available
  const getSelectedPackageFromBusiness = (): string | null => {
    if (business && business.package) {
      return business.package;
    }
    return null;
  };
  
  const [selectedPackage, setSelectedPackage] = useState<string | null>(getSelectedPackageFromBusiness());
  
  // Set a flag when data has fully loaded - now derived from firebaseIsLoading
  // useEffect(() => {
  //   if (!firebaseIsLoading) { // Check if firebase data loading is complete
  //     console.log('useBusinessOnboarding - Firebase data loaded');
  //     // setDataLoaded(true); // dataLoaded is now a const derived from !firebaseIsLoading
  //   }
  // }, [firebaseIsLoading]);
  
  // Update step when URL changes
  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const stepParam = params.get('step');
      if (stepParam) {
        const newStep = parseInt(stepParam, 10);
        console.log('Setting step from URL param:', newStep);
        setStep(newStep);
      }
    } else if (location.pathname.includes('/gocampaign')) {
      // If on GoCampaign page but no step param, default to step 0
      console.log('No step param found, defaulting to step 0');
      setStep(0);
    }
  }, [location.search, location.pathname]);
  
  // Update business when allBusinesses change or businessId changes
  useEffect(() => {
    if (businessId && allBusinesses) { // Ensure allBusinesses is available
      const foundBusiness = findBusiness(businessId);
      console.log('useBusinessOnboarding - findBusiness result:', foundBusiness ? 'found' : 'not found', 'using allBusinesses from FirebaseProvider');
      setBusiness(foundBusiness);
      
      if (foundBusiness?.package) {
        setSelectedPackage(foundBusiness.package);
      } else {
        setSelectedPackage(null); // Reset if business has no package
      }
    } else if (!businessId) {
      setBusiness(undefined); // Clear business if no businessId
      setSelectedPackage(null);
    }
  }, [businessId, allBusinesses, findBusiness]); // findBusiness is memoized
  
  // CRITICAL: Only redirect home if business not found, with better safeguards
  useEffect(() => {
    if (isNavigating) {
      console.log('useBusinessOnboarding - Skipping redirect check during navigation');
      return;
    }
    
    // dataLoaded is now !firebaseIsLoading
    if (businessId && !business && !firebaseIsLoading) { // Check firebaseIsLoading instead of dataLoaded
      console.log('useBusinessOnboarding - Business not found after Firebase data loaded, considering redirect');
      
      const timer = setTimeout(() => {
        const recheckedBusiness = findBusiness(businessId);
        if (!recheckedBusiness) {
          console.log('useBusinessOnboarding - Business still not found after delay, redirecting');
          toast({
            title: "Business not found",
            description: "The business you are trying to onboard does not exist.",
            variant: "destructive"
          });
          navigate('/', { replace: true });
        }
      }, 3000); 
      
      return () => clearTimeout(timer);
    }
  }, [businessId, business, navigate, toast, firebaseIsLoading, isNavigating, findBusiness]); // Use firebaseIsLoading
  
  const handlePackageSelected = async (packageId: string) => { // Made async
    console.log('Setting package in context:', packageId);
    setSelectedPackage(packageId);
    if (businessId) {
      try {
        await businessUtils.updateBusiness(businessId, { package: packageId });
      } catch (error) {
        console.error("Error updating business package:", error);
        toast({ title: "Error", description: "Failed to update package.", variant: "destructive" });
      }
    }
  };
  
  // Navigation function that updates the URL with step parameter, with navigation flag
  const navigateToStep = (stepNumber: number) => {
    console.log(`Explicitly navigating to step ${stepNumber}`);
    
    // First update local state
    setStep(stepNumber);
    
    // Set navigation flag to prevent unwanted redirects
    setIsNavigating(true);
    
    // Force URL update with new path construction
    if (businessId) {
      // Use a consistent URL pattern
      const newUrl = `/business/${businessId}/gocampaign?step=${stepNumber}`;
      console.log(`Navigating to: ${newUrl}`);
      
      // Use replace to avoid navigation stack issues
      navigate(newUrl, { replace: true });
      
      // Reset navigation flag after slight delay
      setTimeout(() => {
        setIsNavigating(false);
      }, 500);
    }
  };
  
  const finishOnboarding = async () => { // Made async
    console.log("finishOnboarding called - displaying completion toast only");
    
    toast({
      title: "GoCampaign Setup Complete",
      description: "You can now launch your campaign from the checkout section",
    });
    
    // We'll only update the business status to show the checkout, 
    // but NOT navigate away from the page yet
    if (businessId) {
      try {
        await businessUtils.updateBusiness(businessId, { 
          goCampaignCompleted: true,
        });
      } catch (error) {
        console.error("Error updating goCampaignCompleted status:", error);
        toast({ title: "Error", description: "Failed to update campaign status.", variant: "destructive" });
      }
    }
    
    // DO NOT navigate away to business details page - we want to stay on the GoCampaign page
    // to show the checkout options
  };
  
  return {
    businessId,
    navigate,
    business,
    step,
    setStep,
    navigateToStep,
    selectedPackage,
    handlePackageSelected,
    handleFinishOnboarding: finishOnboarding,
    isNavigating,
    setIsNavigating
  };
};
